<template>
  <div
    class="calendar-view"
    :class="{ 'show-weekend': showWeekend }"
    :style="{
      '--calendar-card-row-count': `${maxCardsVisible}`,
    }"
  >
    <div class="calendar-view-content">
      <CalendarViewHeader />
      <div
        ref="calendarViewContainer"
        class="calendar-view-container"
      >
        <RecycleScroller
          ref="calendarViewScroller"
          class="calendar-view-scroller"
          item-class="list-card-item-wrap"
          key-field="id"
          :items="items"
          :item-size="weekItemHeight"
          :buffer="buffer"
          :emit-update="true"
          @visible="onVisible"
          @update="handleScrollerUpdate"
        >
          <template #default="{ item }">
            <CalendarWeekItem
              :id="item.id"
              :key="item.id"
              :week-item="item"
              :height="weekItemHeight"
              :max-cards-visible="maxCardsVisible"
            />
          </template>
        </RecycleScroller>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// @ts-nocheck
import { ref, computed, getCurrentInstance, onMounted, onUnmounted } from 'vue';
import { RecycleScroller } from 'vue-virtual-scroller';
import {
  startOfWeek,
  addDays,
  getTime,
  startOfDay,
  eachWeekOfInterval,
  eachDayOfInterval,
  subMonths,
  startOfMonth,
  addMonths,
  endOfDay,
  secondsToMilliseconds,
  isBefore,
} from 'date-fns';
import styleVariables from '@/assets/scss/variables.module.scss';
import { convertStyleToNumber } from '@/utilities/rootStyles';
import EventBus from '@/utilities/eventBus';
import CalendarWeekItem from './CalendarWeekItem.vue';
import CalendarViewHeader from './CalendarViewHeader.vue';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
const { embeddedBoardAttributes, embed, inEpicBoard } = useBoardEmbed();

const _this = getCurrentInstance()!.proxy as any;

const defaultLoadedMonths: number = 24;
const defaultLoadMoreMonths: number = 12;
// default const for trashhold of loading more items (that will trigger when user scroll to the start or end of the list + trashhold)
const defaultLoadMoreTrashhold: number = 50;
const currentDate = Date.now();

const { calendarCardRowGap, calendarDayHeaderHeight, calendarCardHeight } = styleVariables;
const cardRowGap = convertStyleToNumber(calendarCardRowGap);
const dayHeaderHeight = convertStyleToNumber(calendarDayHeaderHeight);
const cardHeight = convertStyleToNumber(calendarCardHeight);

// Changing buffer will change the number of items that are rendered in the list (visible + buffer)
// Changing this can cause jumping behaviour while scrolling and loading items (prepend)
const buffer = ref(1000);
const containerHeight = ref(0);
const calendarViewContainer = ref<HTMLElement | null>(null);
const startEdgeDate = ref<Date>(subMonths(currentDate, defaultLoadedMonths));
const endEdgeDate = ref<Date>(addMonths(currentDate, defaultLoadedMonths));

const currentItemIndex = ref(0);
const calendarFullyLoaded = ref(false);
const loadingItems = ref(false);
const loadingBefore = ref(false);

// Computed
const showWeekend = computed(() => _this.$store.getters.getShowWeekends);

const maxCardsVisible = computed(() => {
  const weekHeight = weekItemHeight.value;
  const maxCards = Math.floor(
    (weekHeight + cardRowGap - dayHeaderHeight) / (cardHeight + cardRowGap)
  );
  return Math.max(0, maxCards - 1);
});

const weekItemHeight = computed(() => {
  const height = Math.floor(containerHeight.value / 6);
  return Math.max(height, 150);
});

const numberOfDays = computed(() => (showWeekend.value ? 7 : 5));

const stateCards = computed(() => _this.$store.getters.getCards);

const boardId = computed(() =>
  embed ? embeddedBoardAttributes.value.boardId : _this.$store.getters.currentBoardId
);
const boardCards = computed(() => {
  return _this.$store.getters.getBoardsCardsIdsByBoardId(boardId.value);
});

const createEdgesSet = (left: boolean, right: boolean) => {
  const edges = new Set();
  if (left) edges.add('left');
  if (right) edges.add('right');
  return edges;
};

const getColumnLayoutAndCroppedEdges = (
  range: { start: number | Date; end: number | Date },
  cardDateRange: { start_date: Date; due_date: Date }
) => {
  const daysOfInterval = eachDayOfInterval(range);

  return daysOfInterval.reduce(
    (acc: any, date: Date, index: number, datesArray: Date[]) => {
      // Initialize column layout and cropped edges if the start is undefined and date is within range
      const dateStartTime = getTime(startOfDay(date));
      const dateEndTime = getTime(endOfDay(date));
      const cardStartTime = getTime(cardDateRange.start_date);
      const cardEndTime = getTime(cardDateRange.due_date);

      if (
        acc.columnLayout.start === undefined &&
        dateStartTime >= cardStartTime &&
        dateStartTime <= cardEndTime
      ) {
        return {
          columnLayout: {
            start: index + 1,
            span: 1,
          },
          croppedEdges: new Set(
            createEdgesSet(
              index === 0 && dateStartTime > cardStartTime,
              index === datesArray.length - 1 && dateEndTime < cardEndTime
            )
          ),
        };
      }
      // Update column layout if already started and date is within range
      else if (acc.columnLayout.start !== undefined && dateEndTime <= cardEndTime) {
        return {
          ...acc,
          columnLayout: {
            ...acc.columnLayout,
            span: (acc.columnLayout.span ?? 0) + 1,
          },
          croppedEdges:
            index === datesArray.length - 1 && dateEndTime < cardEndTime
              ? new Set([...acc.croppedEdges, 'right'])
              : acc.croppedEdges,
        };
      }
      return acc;
    },
    {
      columnLayout: {
        start: undefined,
        span: undefined,
      },
      croppedEdges: new Set(),
    }
  );
};

const generateDateRanges = (cardId: string) => {
  const card = stateCards.value[cardId];
  if (!card) return [];

  const cardStartDate = secondsToMilliseconds(card.start_date);
  const cardDueDate = secondsToMilliseconds(card.due_date);

  if (!cardStartDate && !cardDueDate) return [];

  const start = cardStartDate ? startOfDay(cardStartDate) : startOfDay(cardDueDate);
  const end =
    cardDueDate && cardDueDate >= getTime(start) ? endOfDay(cardDueDate) : endOfDay(cardStartDate);

  // refactor to use reduce instead of map and filter
  const weeks = eachWeekOfInterval({ start, end }, { weekStartsOn: 1 })
    .map((week) => {
      const lastDayOfCurrentWeek = endOfDay(addDays(week, numberOfDays.value - 1));
      if (isBefore(lastDayOfCurrentWeek, start)) return false;

      const { columnLayout, croppedEdges } = getColumnLayoutAndCroppedEdges(
        { start: week, end: lastDayOfCurrentWeek },
        { start_date: start, due_date: end }
      );

      if (columnLayout.start === undefined) return false;
      return {
        weekStartDate: getTime(week),
        columnLayout,
        croppedEdges: croppedEdges,
      };
    })
    .filter(Boolean);

  return weeks;
};

// Methods
const setCalendarContainerHeight = (updateItems = true, recursiveLimitCount = 0) => {
  // 20 is the limit of recursive calls to avoid infinite loop
  if (!calendarViewContainer.value || recursiveLimitCount > 20) return;
  if (!calendarViewContainer.value.clientHeight) {
    requestAnimationFrame(() => {
      setCalendarContainerHeight(updateItems, recursiveLimitCount + 1);
    });
    return;
  }
  containerHeight.value = calendarViewContainer.value.clientHeight;
  buffer.value = containerHeight.value;
  if (updateItems) {
    updateVisibleItems();
  }
};

const onVisible = () => {
  scrollToStart();
};

const getFirstWeekOfMonth = (date = new Date()) => {
  const firstWeekOfMonth = startOfWeek(startOfMonth(date), { weekStartsOn: 1 });
  return getTime(firstWeekOfMonth);
};

const getWeekItemIndex = (weekId: number) => {
  const weeks = _this.items;
  return weeks.findIndex((week: any) => week.id === weekId);
};

const getScrollPosition = (index: number) => {
  const weekHeight = weekItemHeight.value;
  return weekHeight * index;
};

const getItemIndex = (id: number = getFirstWeekOfMonth()) => {
  return getWeekItemIndex(id);
};

const getItemIndexFromPosition = (position: number) => {
  const weekHeight = weekItemHeight.value;
  return Math.floor(position / weekHeight);
};

const setCurrentItemIndex = (index: number) => {
  currentItemIndex.value = index;
};

const handleLoadMoreItems = (index: number) => {
  if (index <= defaultLoadMoreTrashhold) {
    // prepend items
    loadingItems.value = true;
    loadingBefore.value = true;
    prepareScroll();
    startEdgeDate.value = subMonths(startEdgeDate.value, defaultLoadMoreMonths);
  } else if (index >= _this.items.length - defaultLoadMoreTrashhold) {
    // append items
    loadingItems.value = true;
    endEdgeDate.value = addMonths(endEdgeDate.value, defaultLoadMoreMonths);
  }
};

const handleScrollerUpdate = () => {
  if (!calendarFullyLoaded.value) return;
  const { calendarViewScroller } = _this.$refs;
  if (!calendarViewScroller) return;
  const { $_lastUpdateScrollPosition: position } = calendarViewScroller;
  const viewportOffset: number = Math.floor(containerHeight.value * 0.25) + position;
  const index = getItemIndexFromPosition(viewportOffset);
  setCurrentItemIndex(index);
  if (loadingItems.value) {
    loadingItems.value = false;
    return;
  }
  handleLoadMoreItems(index);
};

const scrollToStart = () => {
  const { calendarViewScroller } = _this.$refs;
  if (!calendarViewScroller) return;
  requestAnimationFrame(() => {
    calendarViewScroller.scrollToPosition(getScrollPosition(getItemIndex()));
    calendarFullyLoaded.value = true;
  });
};

const handleChangeMonth = (value: number) => {
  const { calendarViewScroller } = _this.$refs;
  if (!calendarViewScroller || !_this.items.length) return;
  const monthUpdated = addMonths(_this.lastDayOfCurrentWeekFromIndex, value);
  const index = getItemIndex(getFirstWeekOfMonth(monthUpdated));
  requestAnimationFrame(() => {
    calendarViewScroller.scrollToPosition(getScrollPosition(index));
  });
};

EventBus.$on('calendar:moveToStart', scrollToStart);
EventBus.$on('calendar:changeMonth', handleChangeMonth);

const updateVisibleItems = () => {
  const { calendarViewScroller } = _this.$refs;
  if (!calendarViewScroller) return;
  requestAnimationFrame(() => {
    calendarViewScroller.updateVisibleItems(false);
  });
};

const previousScrollHeightMinusTop = ref(0);

const prepareScroll = () => {
  const { calendarViewScroller } = _this.$refs;
  if (!calendarViewScroller) return;
  const element = calendarViewScroller.$el as HTMLElement;
  previousScrollHeightMinusTop.value = element.scrollHeight - element.scrollTop;
};

const restoreScroll = () => {
  if (!loadingBefore.value) return;
  requestAnimationFrame(() => {
    loadingBefore.value = false;
    const { calendarViewScroller } = _this.$refs;
    if (!calendarViewScroller) return;
    const element = calendarViewScroller.$el as HTMLElement;
    element.scrollTop = element.scrollHeight - previousScrollHeightMinusTop.value;
  });
};

// onMounted
const handleUnmounted = () => {
  EventBus.$off('calendar:moveToStart', scrollToStart);
  EventBus.$off('calendar:changeMonth', handleChangeMonth);
};

onMounted(() => {
  setCalendarContainerHeight(false);
});

onUnmounted(handleUnmounted);

defineExpose({
  embed,
  embeddedBoardAttributes,
  stateCards,
  generateDateRanges,
  createEdgesSet,
  startEdgeDate,
  endEdgeDate,
  numberOfDays,
  currentItemIndex,
  loadingItems,
  calendarFullyLoaded,
  loadingBefore,
  restoreScroll,
  inEpicBoard,
  boardCards,
});
</script>
<script lang="ts">
// @ts-nocheck
import boardListMixin from '@/mixins/boardListMixin';
import boardViewMixin from '@/mixins/boardViewMixin';

export default {
  name: 'CalendarView',
  mixins: [boardListMixin, boardViewMixin],
  computed: {
    visible() {
      return null;
    },
    embed() {
      return this._setupProxy.embed.value;
    },
    embeddedBoardAttributes() {
      return this._setupProxy.embeddedBoardAttributes.value;
    },
    inEpicBoard() {
      return this._setupProxy.inEpicBoard.value;
    },
    _setupProxy() {
      return this._?.exposed ?? {};
    },
    board(): any {
      if (this._setupProxy.embed.value) {
        return (
          this.$store.getters.getFlatBoards[
            this._setupProxy.embeddedBoardAttributes?.value.boardId
          ] || {}
        );
      }
      return this.$store.getters.currentBoard;
    },
    boardCardsWithDates(): any {
      return this.listCards.reduce((acc: any, cardId: string) => {
        const card = this._setupProxy?.stateCards.value[cardId];
        const cardStartDate = secondsToMilliseconds(card.start_date);
        const cardDueDate = secondsToMilliseconds(card.due_date);
        if (cardStartDate || cardDueDate) {
          acc.push(card);
        }
        return acc;
      }, []);
    },
    weeksWithCards(): any {
      return this.boardCardsWithDates.reduce((acc: any, card: any) => {
        const cardDateRanges = this._setupProxy?.generateDateRanges(card.id);

        cardDateRanges.forEach((dateRange: any, index: number) => {
          const { weekStartDate, columnLayout, croppedEdges } = dateRange;
          const expandableEdges = this._setupProxy?.createEdgesSet(
            index === 0,
            index === cardDateRanges.length - 1
          );
          const layout = {
            column: columnLayout,
            // default row layout - start from 2nd row because 1st row is for day header
            row: {
              start: 2,
              span: 1,
            },
          };
          const cardWithLayout = {
            calendarTask: card,
            expandableEdges,
            croppedEdges,
            layout,
            style: {},
          };
          const key = getTime(startOfWeek(weekStartDate, { weekStartsOn: 1 }));
          acc[key] = [...(acc[key] ?? []), cardWithLayout];
        });

        return acc;
      }, {});
    },
    items(): any {
      const startEdge: Date = this._setupProxy.startEdgeDate.value;
      const endEdge: Date = this._setupProxy.endEdgeDate.value;

      return eachWeekOfInterval({ start: startEdge, end: endEdge }, { weekStartsOn: 1 }).reduce(
        (acc: any, week: Date) => {
          const weekTime: number = getTime(week);
          const lastDayOfCurrentWeek = endOfDay(
            addDays(week, this._setupProxy?.numberOfDays.value - 1)
          );
          acc.push({
            id: weekTime,
            weekCards: this.weeksWithCards[weekTime] || [],
            lastDayOfCurrentWeek,
          });
          return acc;
        },
        []
      );
    },
    lastDayOfCurrentWeekFromIndex(): any {
      const weeks: any = this.items;
      return weeks[this._setupProxy?.currentItemIndex.value]?.lastDayOfCurrentWeek;
    },
  },
  watch: {
    lastDayOfCurrentWeekFromIndex(newVal: any) {
      if (this._setupProxy.loadingItems.value) return;
      EventBus.$emit('calendar:currentMonthChanged', newVal);
    },
    items(newVal: any) {
      if (
        newVal.length &&
        this._setupProxy.calendarFullyLoaded.value &&
        this._setupProxy.loadingItems.value &&
        this._setupProxy.loadingBefore.value
      ) {
        this._setupProxy.restoreScroll();
      }
    },
  },
};
</script>

<style lang="scss">
.calendar-view {
  @apply grid h-full;
  grid-template-columns: minmax(0, 1fr);

  .calendar-view-content {
    @apply flex flex-col h-full overflow-hidden;

    .calendar-view-container {
      flex: 1 1 100%;
      @apply overflow-hidden relative;
    }
  }

  .calendar-view-content,
  .calendar-view-scroller {
    @apply h-full;
  }
  &:not(.show-weekend) .calendar-day-item {
    &:not(:nth-child(5)) {
      .calendar-day-item-background {
        border-right: 1px solid;
        @apply border-calendar-border;
      }
    }
  }
  &.show-weekend .calendar-day-item {
    &:not(:nth-child(7)) {
      .calendar-day-item-background {
        border-right: 1px solid;
        @apply border-calendar-border;
      }
    }
  }
}
</style>
